import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Pagination,
  Skeleton,
  Table,
  TableCell,
  TableRow,
  Typography,
  usePagination,
  usePathParam
} from '@chilecompra/react-kit'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { REACT_APP_MERCADO_PUBLICO_BASE_URL } from '../../config/settings/environment'
import { formatDate } from '../../modules/utils/formatters'
import { onGetReviewedContractsThunk } from './ReviewedContractsTable.actions'
import { ExternalLink, Link, Strong, SubHeadingWrapper } from './ReviewedContractsTable.styles'
import { onOpenHistoricalContractsDialog } from '../HistoricalContracts/HistoricalContracts.actions'
import HistoricalContractsDialog from '../HistoricalContracts/HistoricalContractsDialog.container'
import { USER_TYPE } from '../../config/settings/constant'
import useReCaptcha from '../../modules/hooks/reCaptcha'

/**
 * The reviewed contracts table's container.
 */
export const ReviewedContractsTable = () => {
  const { userType } = useAuthorization()
  const { executeReCaptcha } = useReCaptcha()
  const { months } = useSelector(state => state.contractualBehavior)
  const { loadingReviewsList, reviewedContractsList } = useSelector(state => state.reviewedContracts)
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const rowsPerPage = 5

  const { page, totalPages, onChangePage, onGetCurrentPage } = usePagination({
    data: reviewedContractsList,
    initialPage: 1,
    rowsPerPage
  })

  const handleOpenHistorical = () => {
    dispatch(onOpenHistoricalContractsDialog())
  }

  useEffect(() => {
    if (!loadingReviewsList || userType === '') return
    if (userType === USER_TYPE.PUBLIC) {
      const executeAll = async () => {
        await executeReCaptcha({ action: 'publicInfo' })
        dispatch(onGetReviewedContractsThunk({ dni }))
      }
      executeAll()
    } else {
      const executeAll = () => {
        dispatch(onGetReviewedContractsThunk({ dni }))
      }
      executeAll()
    }
  }, [userType])

  return (
    <>
      <Typography fontWeight="bold" padding="0 0 16px" variant="h4">
        Comportamiento por evaluación de contratos
      </Typography>
      <SubHeadingWrapper>
        <Typography variant="body2">
          {`Estas son las evaluaciones obtenidas desde las fichas de contrato evaluadas en el Mercado Público en ${
            months === 1 ? 'el último mes' : `los últimos ${months} meses`
          }. Si quieres gestionar o revisar más detalles, puedes hacerlo directamente desde el `}
          <Strong>menú Contratos de tu escritorio.</Strong>
        </Typography>
      </SubHeadingWrapper>
      <Typography fontWeight="bold" margin="24px 0 12px" variant="h4">
        Detalle de contratos evaluados
      </Typography>
      {loadingReviewsList ? (
        <Skeleton variant="rect" height={150} />
      ) : !loadingReviewsList && reviewedContractsList.length !== 0 ? (
        <>
          <Table accessibility={{ label: 'Detalle de contratos evaluados' }} margin="28px 0 8px">
            <TableRow>
              <TableCell width="100px">
                <Typography fontWeight="bold" variant="caption">
                  Id contrato
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight="bold" variant="caption">
                  Nombre contrato
                </Typography>
              </TableCell>
              <TableCell width="110px">
                <Typography fontWeight="bold" variant="caption">
                  Fecha evaluación
                </Typography>
              </TableCell>
              <TableCell width="40px">
                <Typography fontWeight="bold" variant="caption">
                  Nota
                </Typography>
              </TableCell>
              <TableCell width="70px">
                <Typography fontWeight="bold" variant="caption">
                  Acciones
                </Typography>
              </TableCell>
            </TableRow>
            {onGetCurrentPage().map((contract, index) => {
              return (
                <TableRow key={`contract-review-${index}`}>
                  <TableCell>
                    <Strong display="block">{contract.dni}</Strong>
                  </TableCell>
                  <TableCell>
                    <Strong>{contract.contractName}</Strong>
                  </TableCell>
                  <TableCell>{formatDate(contract.publicationDate)}</TableCell>
                  <TableCell>{contract.contractScore}</TableCell>
                  <TableCell>
                    <ExternalLink
                      href={`${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Contratos/GestionContratos/DetalleContrato?idContrato=${contract.contractId}`}
                      target="_blank"
                    >
                      Ver detalle
                    </ExternalLink>
                  </TableCell>
                </TableRow>
              )
            })}
          </Table>
          {reviewedContractsList?.length > rowsPerPage && (
            <Pagination alignment="left" count={totalPages} page={page} onChange={onChangePage} />
          )}
        </>
      ) : (
        <Typography variant="body2" color="black3">
          {`Esta empresa proveedora no ha recibido evaluaciones o no tiene contratos vigentes en Mercado Público en ${
            months === 1 ? 'el último mes' : `los últimos ${months} meses`
          }.`}
        </Typography>
      )}

      <HistoricalContractsDialog />
      <Link onClick={handleOpenHistorical}>Ver contratos históricos</Link>
    </>
  )
}

export default ReviewedContractsTable
